import React, { useState, useRef, useCallback } from 'react';
import styled, { createGlobalStyle } from 'styled-components';
import { MapPin, X as XIcon, Radio } from 'lucide-react';
import { useTheme } from '../../context/ThemeContext';
import { GoogleMap, Marker, Autocomplete, useJsApiLoader } from '@react-google-maps/api';

const GOOGLE_MAPS_LIBRARIES = ['places'];
const DEFAULT_CENTER = { lat: 43.65, lng: -79.38 };

// Ensure Google's autocomplete dropdown appears above the modal
const PacContainerStyle = createGlobalStyle`
  .pac-container {
    z-index: 10001 !important;
  }
`;

// ==================== Styled Components ====================

const Overlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.6);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 10000;
  padding: ${props => props.theme.spacing.md};
`;

const ModalCard = styled.div`
  background: ${props => props.theme.colors.background};
  border-radius: ${props => props.theme.borderRadius.lg};
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.35);
  width: 100%;
  max-width: 700px;
  max-height: 90vh;
  display: flex;
  flex-direction: column;
`;

const ModalHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: ${props => props.theme.spacing.md} ${props => props.theme.spacing.lg};
  border-bottom: 1px solid ${props => props.theme.colors.border};
  flex-shrink: 0;
`;

const ModalTitle = styled.span`
  font-weight: 600;
  font-size: 17px;
  color: ${props => props.theme.colors.text};
`;

const CloseBtn = styled.button`
  background: none;
  border: none;
  cursor: pointer;
  font-size: 20px;
  color: ${props => props.theme.colors.muted};
  line-height: 1;
  padding: 2px 6px;

  &:hover {
    color: ${props => props.theme.colors.text};
  }
`;

const ModalBody = styled.div`
  flex: 1;
  overflow-y: auto;
  padding: ${props => props.theme.spacing.md} ${props => props.theme.spacing.lg};
  display: flex;
  flex-direction: column;
  gap: ${props => props.theme.spacing.md};
`;

const SearchRow = styled.div`
  display: flex;
  gap: ${props => props.theme.spacing.sm};
  align-items: stretch;
`;

const SearchInput = styled.input`
  flex: 1;
  padding: ${props => props.theme.spacing.sm} ${props => props.theme.spacing.md};
  border: 1px solid ${props => props.theme.colors.border};
  border-radius: ${props => props.theme.borderRadius.md};
  background: ${props => props.theme.colors.background};
  color: ${props => props.theme.colors.text};
  font-size: ${props => props.theme.typography.sizes.default};

  &:focus {
    outline: none;
    border-color: ${props => props.theme.colors.tint};
  }
`;

const GpsBtn = styled.button`
  padding: ${props => props.theme.spacing.sm} ${props => props.theme.spacing.md};
  border: 1px solid ${props => props.theme.colors.border};
  border-radius: ${props => props.theme.borderRadius.md};
  background: ${props => props.theme.colors.background};
  color: ${props => props.theme.colors.text};
  cursor: pointer;
  font-size: 13px;
  white-space: nowrap;
  flex-shrink: 0;

  &:hover:not(:disabled) {
    border-color: ${props => props.theme.colors.tint};
    background: ${props => props.theme.colors.tint + '12'};
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const MapWrapper = styled.div`
  width: 100%;
  height: 370px;
  border-radius: ${props => props.theme.borderRadius.md};
  overflow: hidden;
  border: 1px solid ${props => props.theme.colors.border};
  flex-shrink: 0;
`;

const MapPlaceholder = styled.div`
  width: 100%;
  height: 370px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: ${props => props.theme.borderRadius.md};
  border: 1px solid ${props => props.theme.colors.border};
  background: ${props => props.theme.colors.backgroundSecondary || '#f5f5f5'};
  color: ${props => props.theme.colors.muted};
  font-size: 14px;
  flex-shrink: 0;
`;

const CoordBar = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: ${props => props.theme.spacing.md};
  align-items: center;
  padding: ${props => props.theme.spacing.sm} ${props => props.theme.spacing.md};
  background: ${props => props.theme.colors.backgroundSecondary || '#f5f5f5'};
  border-radius: ${props => props.theme.borderRadius.md};
  font-size: 13px;
`;

const CoordLabel = styled.span`
  color: ${props => props.theme.colors.muted};
`;

const CoordVal = styled.span`
  font-weight: 600;
  font-family: monospace;
  color: ${props => props.theme.colors.text};
`;

const HintText = styled.p`
  font-size: 13px;
  color: ${props => props.theme.colors.muted};
  margin: 0;
  text-align: center;
  padding: ${props => props.theme.spacing.sm} 0;
`;

const ModalFooter = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: ${props => props.theme.spacing.md};
  padding: ${props => props.theme.spacing.md} ${props => props.theme.spacing.lg};
  border-top: 1px solid ${props => props.theme.colors.border};
  flex-shrink: 0;
`;

const FooterBtn = styled.button`
  padding: 8px 20px;
  border-radius: ${props => props.theme.borderRadius.md};
  border: 1px solid ${props => props.$primary ? 'transparent' : props.theme.colors.border};
  background: ${props => props.$primary ? props.theme.colors.tint : props.theme.colors.background};
  color: ${props => props.$primary ? '#ffffff' : props.theme.colors.text};
  font-size: ${props => props.theme.typography.sizes.default};
  font-weight: 500;
  cursor: pointer;

  &:hover:not(:disabled) {
    opacity: 0.85;
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

// ==================== Component ====================

/**
 * LocationPickerModal — Google Maps-based pin picker for form location fields.
 *
 * @param {Function} onSave - Called with { latitude, longitude, accuracy, locationName }
 * @param {Function} onClose - Called when modal is dismissed
 * @param {Object|null} initialValue - Existing location value to pre-populate
 */
const LocationPickerModal = ({ onSave, onClose, initialValue = null }) => {
  const theme = useTheme();
  const autocompleteRef = useRef(null);

  const { isLoaded } = useJsApiLoader({
    googleMapsApiKey: import.meta.env.VITE_GOOGLE_MAPS_API_KEY || '',
    libraries: GOOGLE_MAPS_LIBRARIES,
  });

  const [markerPosition, setMarkerPosition] = useState(
    initialValue?.latitude && initialValue?.longitude
      ? { lat: initialValue.latitude, lng: initialValue.longitude }
      : null
  );
  const [locationName, setLocationName] = useState(initialValue?.locationName || '');
  const [accuracy, setAccuracy] = useState(initialValue?.accuracy || null);
  const [gpsLoading, setGpsLoading] = useState(false);

  const mapCenter = markerPosition || DEFAULT_CENTER;
  const mapZoom = markerPosition ? 15 : 4;

  // ---- Reverse geocode helper ----

  const reverseGeocode = (lat, lng) => {
    if (!window.google?.maps) return;
    const geocoder = new window.google.maps.Geocoder();
    geocoder.geocode({ location: { lat, lng } }, (results, status) => {
      if (status === 'OK' && results[0]) {
        setLocationName(results[0].formatted_address);
      }
    });
  };

  // ---- Autocomplete ----

  const onAutocompleteLoad = useCallback((ac) => {
    autocompleteRef.current = ac;
    ac.setFields(['geometry', 'formatted_address']);
  }, []);

  const onPlaceChanged = useCallback(() => {
    const ac = autocompleteRef.current;
    if (!ac) return;
    const place = ac.getPlace();
    if (!place?.geometry?.location) return;
    const lat = place.geometry.location.lat();
    const lng = place.geometry.location.lng();
    setMarkerPosition({ lat, lng });
    setLocationName(place.formatted_address || '');
    setAccuracy(null);
  }, []);

  // ---- Map interaction ----

  const handleMapClick = useCallback((e) => {
    const lat = e.latLng.lat();
    const lng = e.latLng.lng();
    setMarkerPosition({ lat, lng });
    setAccuracy(null);
    reverseGeocode(lat, lng);
  }, []); // eslint-disable-line

  const handleMarkerDragEnd = useCallback((e) => {
    const lat = e.latLng.lat();
    const lng = e.latLng.lng();
    setMarkerPosition({ lat, lng });
    setAccuracy(null);
    reverseGeocode(lat, lng);
  }, []); // eslint-disable-line

  // ---- GPS ----

  const handleUseMyLocation = () => {
    if (!navigator.geolocation) {
      alert('Geolocation is not supported by your browser.');
      return;
    }
    setGpsLoading(true);
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        const lat = pos.coords.latitude;
        const lng = pos.coords.longitude;
        setMarkerPosition({ lat, lng });
        setAccuracy(pos.coords.accuracy);
        setGpsLoading(false);
        reverseGeocode(lat, lng);
      },
      () => {
        setGpsLoading(false);
        alert('Unable to get your location. Please enable location services and try again.');
      },
      { enableHighAccuracy: true, timeout: 10000 }
    );
  };

  // ---- Save ----

  const handleSave = () => {
    if (!markerPosition) {
      alert('Please pick a location on the map first.');
      return;
    }
    onSave({
      latitude: markerPosition.lat,
      longitude: markerPosition.lng,
      accuracy,
      locationName: locationName || null,
    });
  };

  // ==================== Render ====================

  return (
    <Overlay theme={theme}>
      <PacContainerStyle />
      <ModalCard theme={theme}>
        <ModalHeader theme={theme}>
          <ModalTitle theme={theme}><MapPin size={18} style={{ verticalAlign: 'middle', marginRight: 6 }} />Pick Location</ModalTitle>
          <CloseBtn theme={theme} onClick={onClose}><XIcon size={18} /></CloseBtn>
        </ModalHeader>

        <ModalBody theme={theme}>
          {/* Search bar + GPS button */}
          <SearchRow theme={theme}>
            {isLoaded ? (
              <Autocomplete
                onLoad={onAutocompleteLoad}
                onPlaceChanged={onPlaceChanged}
                style={{ flex: 1 }}
              >
                <SearchInput theme={theme} type="text" placeholder="Search for an address…" />
              </Autocomplete>
            ) : (
              <SearchInput theme={theme} type="text" placeholder="Loading Google Maps…" disabled />
            )}
            <GpsBtn theme={theme} onClick={handleUseMyLocation} disabled={gpsLoading}>
              {gpsLoading ? '...' : <><Radio size={14} style={{ verticalAlign: 'middle', marginRight: 4 }} />Use My Location</>}
            </GpsBtn>
          </SearchRow>

          {/* Map */}
          {isLoaded ? (
            <MapWrapper theme={theme}>
              <GoogleMap
                mapContainerStyle={{ width: '100%', height: '100%' }}
                center={mapCenter}
                zoom={mapZoom}
                onClick={handleMapClick}
              >
                {markerPosition && (
                  <Marker
                    position={markerPosition}
                    draggable
                    onDragEnd={handleMarkerDragEnd}
                  />
                )}
              </GoogleMap>
            </MapWrapper>
          ) : (
            <MapPlaceholder theme={theme}>Loading map…</MapPlaceholder>
          )}

          {/* Coordinates / hint */}
          {markerPosition ? (
            <CoordBar theme={theme}>
              <CoordLabel theme={theme}>Lat: <CoordVal theme={theme}>{markerPosition.lat.toFixed(6)}</CoordVal></CoordLabel>
              <CoordLabel theme={theme}>Lng: <CoordVal theme={theme}>{markerPosition.lng.toFixed(6)}</CoordVal></CoordLabel>
              {accuracy != null && (
                <CoordLabel theme={theme}>Accuracy: <CoordVal theme={theme}>±{Math.round(accuracy)} m</CoordVal></CoordLabel>
              )}
              {locationName && (
                <CoordLabel
                  theme={theme}
                  style={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
                >
                  {locationName}
                </CoordLabel>
              )}
            </CoordBar>
          ) : (
            <HintText theme={theme}>
              Click on the map, drag the marker, or search for an address to place a pin.
            </HintText>
          )}
        </ModalBody>

        <ModalFooter theme={theme}>
          <FooterBtn theme={theme} onClick={onClose}>Cancel</FooterBtn>
          <FooterBtn theme={theme} $primary onClick={handleSave} disabled={!markerPosition}>
            Save Location
          </FooterBtn>
        </ModalFooter>
      </ModalCard>
    </Overlay>
  );
};

export default LocationPickerModal;
export { LocationPickerModal };
